// ============================================================================
// CLIENT-SAFE TICKET GRAPHQL DOCUMENTS
// ============================================================================
//
// The Hub ticket list/detail surfaces a `client_user` can reach (`/tickets`,
// `/tickets/[id]`) fetch through the Apollo entity-hooks path (GetTicketss /
// GetTickets). Those documents select internal/staff-only columns
// (internal_notes, hourly_rate, total_cost, …). RLS is row-level and cannot
// mask columns, so for client reads we prune those scalar fields from the
// GraphQL selection set at runtime — the pruned columns then never appear in
// the request or the network response (not merely hidden in the UI).
//
// SECURITY NOTE: this is an app-layer projection, NOT a server-enforced
// boundary. A client holding a valid Supabase JWT can still query these columns
// directly against pg_graphql. The durable fix belongs in the DB. See the
// security review accompanying this change. `customer_visible_notes` is the
// client-facing note and is intentionally retained.
// ============================================================================

import { visit, type DocumentNode } from "graphql";
import { CLIENT_HIDDEN_TICKET_COLUMNS } from "@wtc/supabase";
import { GetTicketssDocument, GetTicketsDocument } from "@/src/gql/graphql";

const HIDDEN_TICKET_COLUMNS: ReadonlySet<string> = new Set(CLIENT_HIDDEN_TICKET_COLUMNS);

/**
 * Return a copy of `doc` with internal/staff-only ticket SCALAR fields removed
 * from every selection set. Only leaf fields (no sub-selection) are pruned, so
 * a relation that happens to share a name is preserved. These column names are
 * ticket-specific within the tickets GraphQL documents, so a document-wide prune
 * is safe here.
 */
export function pruneInternalTicketFields(doc: DocumentNode): DocumentNode {
  return visit(doc, {
    Field(node) {
      if (!node.selectionSet && HIDDEN_TICKET_COLUMNS.has(node.name.value)) {
        return null; // drop this field node from the selection set
      }
      return undefined; // leave everything else untouched
    },
  });
}

// Computed once at module load. Cast back to the typed document node so the
// Apollo hooks keep their generated result/variables typing — the pruned AST is
// the same query, just with internal scalar fields removed.
export const GetTicketssClientDocument =
  pruneInternalTicketFields(GetTicketssDocument) as typeof GetTicketssDocument;
export const GetTicketsClientDocument =
  pruneInternalTicketFields(GetTicketsDocument) as typeof GetTicketsDocument;
